import { parseHex } from '../colors';
import { token } from '../theme/tokens';
import { oklchToHex } from '../theme/oklch';
import type { Category } from './types';

/**
 * Pluggy-aligned canonical taxonomy. Every one of Pluggy's 21 real parent
 * categories (ids `01000000`..`21000000`, verified against `pluggy_categories`
 * in the DB) has a first-class chip here, so any Pluggy parent resolves to a
 * meaningful bucket instead of folding into Outros. On top of the parents we
 * keep a handful of GRANULAR children (restaurante, delivery, combustível,
 * farmácia, streaming) that have no Pluggy parent of their own — they resolve
 * via the description map (`PLUGGY_DESCRIPTION_TO_ID`), shading off their
 * parent's hue in breakdowns.
 *
 * The hidden `uncategorized` entry is internal-only — `getCategory()` returns
 * it when a Pluggy id can't be mapped. It does not appear in the filter
 * drawer (consumers list `CATEGORIES`, not `ALL_CATEGORIES`).
 *
 * Server-side icon ownership: each `iconName` is the kebab-case Phosphor
 * identifier resolved by `src/components/CategoryIcon.tsx`. The vocabulary
 * matches `pluggy_categories.icon` in the DB so chip icons stay consistent
 * with leaf-transaction icons.
 */
export const CATEGORIES: readonly Category[] = [
  // Essenciais
  { id: 'mercado',           label: 'Mercado',           color: '#A33460', group: 'essentials', iconName: 'shopping-cart',          pluggyParentId: '10000000' },
  { id: 'restaurante',       label: 'Restaurante',       color: '#EE7D55', group: 'essentials', iconName: 'fork-knife' },
  { id: 'delivery',          label: 'Delivery',          color: '#782600', group: 'essentials', iconName: 'bicycle' },
  { id: 'alimentacao',       label: 'Alimentos e bebidas', color: '#B74B21', group: 'essentials', iconName: 'fork-knife',          pluggyParentId: '11000000' },
  { id: 'transporte',        label: 'Transporte',        color: '#BD7221', group: 'essentials', iconName: 'car',                    pluggyParentId: '19000000' },
  { id: 'combustivel',       label: 'Combustível',       color: '#794300', group: 'essentials', iconName: 'gas-pump' },
  { id: 'moradia',           label: 'Moradia',           color: '#783818', group: 'essentials', iconName: 'house',                  pluggyParentId: '17000000' },
  { id: 'saude',             label: 'Saúde',             color: '#C0524F', group: 'essentials', iconName: 'heartbeat',              pluggyParentId: '18000000' },
  { id: 'farmacia',          label: 'Farmácia',          color: '#8D2226', group: 'essentials', iconName: 'pill' },
  { id: 'servicos',          label: 'Serviços',          color: '#E39193', group: 'essentials', iconName: 'wrench',                 pluggyParentId: '07000000' },
  { id: 'seguros',           label: 'Seguros',           color: '#435AB8', group: 'essentials', iconName: 'shield',                 pluggyParentId: '20000000' },

  // Lazer & Pessoal
  { id: 'assinaturas',       label: 'Assinaturas',       color: '#7B5BA1', group: 'lifestyle',  iconName: 'receipt' },
  { id: 'streaming',         label: 'Streaming',         color: '#AB8BD5', group: 'lifestyle',  iconName: 'music-note' },
  { id: 'servicos-digitais', label: 'Serviços digitais', color: '#7A6001', group: 'lifestyle',  iconName: 'monitor',               pluggyParentId: '09000000' },
  { id: 'lazer',             label: 'Lazer',             color: '#533A8C', group: 'lifestyle',  iconName: 'confetti',               pluggyParentId: '21000000' },
  { id: 'viagens',           label: 'Viagens',           color: '#AE9FE4', group: 'lifestyle',  iconName: 'airplane',              pluggyParentId: '12000000' },
  { id: 'apostas',           label: 'Apostas',           color: '#9F7DD8', group: 'lifestyle',  iconName: 'dice-five',             pluggyParentId: '14000000' },
  { id: 'compras',           label: 'Compras',           color: '#B8985B', group: 'lifestyle',  iconName: 'shopping-bag',          pluggyParentId: '08000000' },
  { id: 'educacao',          label: 'Educação',          color: '#762B67', group: 'lifestyle',  iconName: 'graduation-cap' },

  // Crescimento & Receitas (income → reserved green/teal band; investments count as income)
  { id: 'investimento',      label: 'Investimento',      color: '#17A7A1', group: 'income',     iconName: 'trend-up',              pluggyParentId: '03000000' },
  { id: 'renda',             label: 'Renda',             color: '#1B9247', group: 'income',     iconName: 'currency-circle-dollar', pluggyParentId: '01000000' },

  // Financial / money-system (transfers, loans, obligations, taxes, fees) → blue/cool
  { id: 'transferencia',     label: 'Transferências',    color: '#5B7FB8', group: 'transfer',   iconName: 'arrows-left-right',     pluggyParentId: '04000000' },
  { id: 'emprestimos',       label: 'Empréstimos',       color: '#015475', group: 'transfer',   iconName: 'bank',                  pluggyParentId: '02000000' },
  { id: 'obrigacoes',        label: 'Obrigações legais', color: '#006C95', group: 'transfer',   iconName: 'scales',                pluggyParentId: '06000000' },
  { id: 'doacoes',           label: 'Doações',           color: '#C96EA9', group: 'transfer',   iconName: 'handshake',             pluggyParentId: '13000000' },
  { id: 'impostos',          label: 'Impostos',          color: '#0F9ED8', group: 'transfer',   iconName: 'receipt',               pluggyParentId: '15000000' },
  { id: 'taxas',             label: 'Taxas bancárias',   color: '#66B5E0', group: 'transfer',   iconName: 'buildings',             pluggyParentId: '16000000' },
] as const;

/**
 * Internal fallback for unmapped Pluggy paths. Hidden from the filter drawer
 * (consumers iterate `CATEGORIES`). `getCategory(unknownId)` returns this.
 */
export const FALLBACK_CATEGORY: Category = {
  id: 'uncategorized',
  label: 'Outros',
  color: '#A8ABA8', // muted neutral-400 — uncategorized/null reads as "no color", not a hue
  group: 'uncategorized',
  iconName: 'circle-dashed',
};

/** Includes the hidden fallback — exported for completeness; UI uses `CATEGORIES`. */
export const ALL_CATEGORIES: readonly Category[] = [...CATEGORIES, FALLBACK_CATEGORY];

export const CATEGORY_BY_ID: ReadonlyMap<string, Category> = new Map(
  ALL_CATEGORIES.map((c) => [c.id, c]),
);

/**
 * The taxonomy now owns an explicit chip for all 21 of Pluggy's real parent
 * categories (`01000000`..`21000000`). The single remaining unmapped parent is
 * `05000000` (Transferências — sends to third parties), which folds into the
 * `transferencia` chip alongside `04000000` (same-ownership transfers). Any
 * future Pluggy parent without its own chip can be routed here.
 */
const PLUGGY_PARENT_FALLBACKS: Record<string, string> = {
  '05000000': 'transferencia', // Transferências (to third parties) → fold into transfers
};

const CATEGORY_BY_PLUGGY_PARENT: ReadonlyMap<string, Category> = (() => {
  const map = new Map<string, Category>(
    CATEGORIES.flatMap((c) => (c.pluggyParentId ? [[c.pluggyParentId, c]] as const : [])),
  );
  for (const [parentId, chipId] of Object.entries(PLUGGY_PARENT_FALLBACKS)) {
    if (map.has(parentId)) continue; // never override an explicit chip mapping
    const chip = CATEGORIES.find((c) => c.id === chipId);
    if (chip) map.set(parentId, chip);
  }
  return map;
})();

export function getCategory(id: string | null | undefined): Category {
  if (!id) return FALLBACK_CATEGORY;
  return CATEGORY_BY_ID.get(id) ?? FALLBACK_CATEGORY;
}

/**
 * Pluggy 8-digit id → frontend chip id. Maps the parent prefix (first 2
 * digits + 000000) to a chip. Leaf-level splits (e.g. farmacia vs saude)
 * defer to a follow-up that wires the full leaf id through.
 *
 * Unknown parents return `uncategorized` and fire telemetry.
 */
export function mapPluggyCategoryId(pluggyCategoryId: string | null | undefined): string {
  if (!pluggyCategoryId) return FALLBACK_CATEGORY.id;
  const id = String(pluggyCategoryId).trim();
  if (!id) return FALLBACK_CATEGORY.id;

  const parentId = id.length >= 2 ? `${id.slice(0, 2)}000000` : id;
  const hit = CATEGORY_BY_PLUGGY_PARENT.get(parentId) ?? CATEGORY_BY_PLUGGY_PARENT.get(id);
  if (hit) return hit.id;
  emitUnmappedTelemetry(id);
  return FALLBACK_CATEGORY.id;
}

/**
 * Pluggy description path → frontend chip id. Walks the path tail-first so
 * a leaf miss still resolves via its parent.
 */
const PLUGGY_DESCRIPTION_TO_ID: Record<string, string> = {
  // Essenciais — food split
  'Food': 'mercado', 'Food and Drinks': 'mercado',
  'Restaurants': 'restaurante', 'Restaurant': 'restaurante',
  'Groceries': 'mercado', 'Supermarket': 'mercado',
  'Delivery': 'delivery', 'iFood': 'delivery', 'Rappi': 'delivery',

  // Essenciais — moradia / bills (assinaturas)
  'Home': 'moradia', 'Housing': 'moradia', 'Rent': 'moradia', 'Mortgage': 'moradia',
  'Utilities': 'assinaturas', 'Bills': 'assinaturas',
  'Electricity': 'assinaturas', 'Water': 'assinaturas', 'Internet': 'assinaturas', 'Phone': 'assinaturas',

  // Essenciais — serviços / seguros
  'Services': 'servicos',
  'Insurance': 'seguros',

  // Transporte split
  'Transport': 'transporte', 'Transportation': 'transporte',
  'Public Transport': 'transporte', 'Ride Sharing': 'transporte', 'Uber': 'transporte',
  'Fuel': 'combustivel', 'Gas': 'combustivel', 'Gasoline': 'combustivel',

  // Saúde split
  'Health': 'saude', 'Healthcare': 'saude', 'Medical': 'saude',
  'Pharmacy': 'farmacia', 'Drugstore': 'farmacia',

  // Lazer & Pessoal
  'Education': 'educacao', 'Books': 'educacao', 'Courses': 'educacao',
  'Leisure': 'lazer', 'Entertainment': 'lazer',
  'Streaming': 'streaming', 'Netflix': 'streaming', 'Spotify': 'streaming',
  'Digital services': 'servicos-digitais', 'Games': 'servicos-digitais',
  'RecurringPayments': 'assinaturas',
  'Travel': 'viagens', 'Hotels': 'viagens', 'Flights': 'viagens',
  'Gambling': 'apostas', 'Lottery': 'apostas',
  'Shopping': 'compras', 'Clothing': 'compras', 'Electronics': 'compras',

  // Receitas / Crescimento
  'Income': 'renda', 'Salary': 'renda', 'Payroll': 'renda',
  'Investments': 'investimento', 'Dividends': 'investimento',
  'Refund': 'renda', 'Reimbursement': 'renda',
  'Savings': 'investimento',
  'Loans': 'emprestimos',

  // Obrigações / impostos / taxas / doações
  'Taxes': 'impostos',
  'Bank fees': 'taxas',
  'Donations': 'doacoes',

  // Transferências
  'Transfer': 'transferencia', 'Transfers': 'transferencia',
  'Pix': 'transferencia', 'TED': 'transferencia', 'DOC': 'transferencia',
};

export function mapPluggyCategoryDescription(path: string | readonly string[] | null | undefined): string {
  if (!path) return FALLBACK_CATEGORY.id;
  const parts: readonly string[] = Array.isArray(path)
    ? (path as readonly string[])
    : String(path).split('>').map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return FALLBACK_CATEGORY.id;
  for (let i = parts.length - 1; i >= 0; i--) {
    const hit = PLUGGY_DESCRIPTION_TO_ID[parts[i]!];
    if (hit) return hit;
  }
  emitUnmappedTelemetry(parts.join(' > '));
  return FALLBACK_CATEGORY.id;
}

/**
 * Back-compat: accepts either an 8-digit id or a description / description
 * path. Prefer the typed siblings above when you know what you have.
 */
export function mapPluggyCategory(input: string | readonly string[] | null | undefined): string {
  if (!input) return FALLBACK_CATEGORY.id;
  if (typeof input === 'string' && /^\d{8}$/.test(input.trim())) {
    return mapPluggyCategoryId(input);
  }
  return mapPluggyCategoryDescription(input);
}

declare global {
  interface Window {
    verticeTelemetry?: { track: (event: string, payload?: unknown) => void };
  }
}

function emitUnmappedTelemetry(value: string): void {
  if (typeof window === 'undefined') return;
  window.verticeTelemetry?.track('unmappedPluggyCategory', { value });
}

// ── chip styling derived from the brand color ────────────────────────────────

// Category id → `--category-<key>` theme token (mostly identity; a couple of
// token names differ from the id). The static `color` field is the fallback.
const CATEGORY_TOKEN: Record<string, string> = {
  transferencia: 'transferencias',
  uncategorized: 'outros',
};

/** Category accent resolved from the live theme token, so the palette is authoritative. */
function categoryAccent(cat: Category): string {
  return token(`--category-${CATEGORY_TOKEN[cat.id] ?? cat.id}`, cat.color);
}

export interface CategoryChipStyle {
  backgroundColor: string;
  color: string;
  accent: string;
  /** Small category dot fill: the TRUE hue in light, lifted toward white in dark
   *  (--category-dot-lift). Sanitized `color-mix`, so it re-resolves on a theme flip. */
  dot: string;
}

/**
 * THE chip/tile style derivation, shared by every category surface. Feed it the
 * color from `resolveCategoryColor` (or a `CategoryMeta.color`) — never restyle
 * by hand. The accent (verbatim brand hue) is theme-independent; the tint and
 * glyph adapt to the active theme so the icon keeps ≥3:1 against its own disk:
 *  - light: a faint tint with a darkened glyph (dark-on-light),
 *  - dark: a stronger tint on the near-black row with a lightened glyph
 *    (light-on-dark) — the mid-dark category hues would otherwise sink into the
 *    near-black surface and vanish.
 *
 * The theme adaptation is expressed with `color-mix` against the CSS knobs
 * defined in globals.css (`--category-chip-tint`, `--category-glyph-keep`,
 * `--category-glyph-ink`) — NOT a runtime `data-theme` read — so the derivation
 * stays a pure function and the inline colors re-resolve automatically when the
 * theme toggles, without every consumer having to subscribe to theme changes.
 * `color-mix(in srgb, C 65%, #000)` == the old `darken(C, 0.35)`; against #FFFFFF
 * at 58% == the old `lighten(C, 0.42)` — same values, now theme-reactive in CSS.
 *
 * The hue is sanitized to a `#rrggbb` first (malformed stored colors degrade to
 * the slate fallback via `parseHex`), so the interpolated `color-mix` is always
 * valid CSS. The `accent` stays the verbatim input.
 */
export function categoryChipStyleFromColor(color: string): CategoryChipStyle {
  const [r, g, b] = parseHex(color);
  const hex = `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  return {
    backgroundColor: `color-mix(in srgb, ${hex} var(--category-chip-tint), transparent)`,
    color: `color-mix(in srgb, ${hex} var(--category-glyph-keep), var(--category-glyph-ink))`,
    accent: color,
    dot: `color-mix(in srgb, ${hex}, #fff var(--category-dot-lift, 0%))`,
  };
}

/**
 * THE category color: a custom category's user-picked hex when present (blank
 * counts as "no color"), otherwise the deterministic hierarchical palette
 * (`hashToCategoryColor` — parent anchors, child lightness shades).
 */
export function resolveCategoryColor(
  ref: string,
  source?: 'pluggy' | 'custom',
  customColor?: string | null,
): string {
  return source === 'custom' && customColor ? customColor : hashToCategoryColor(ref);
}

export function getCategoryChipStyle(id: string): CategoryChipStyle {
  return categoryChipStyleFromColor(categoryAccent(getCategory(id)));
}

export function getCategoryChartColor(id: string): string {
  return categoryAccent(getCategory(id));
}

export function getCategoryLabel(id: string): string {
  return getCategory(id).label;
}

export function getCategoryIconName(id: string): string {
  return getCategory(id).iconName;
}

// The categorical color envelope (OKLCH). A category's HUE is SEMANTIC: income/
// positive parents sit in the COOL band (green→blue), expense parents in the WARM/
// purple band — so a color alone says "money in" vs "money out". (Superseded: the
// palette now spans the FULL wheel for maximum distinctness — income/expense direction
// is carried by the signed-amount colors, not category hue.) Each parent has a fixed
// {L,C,H} chosen by greedy max-min so even co-occurring categories never look alike; a
// SUB-category keeps the parent hue+chroma and shifts LIGHTNESS (a clearly lighter/darker
// shade). The table mirrors the `--category-*` token generator — so token == runtime.
const DEFAULT_L = 0.585;
const DEFAULT_C = 0.135;
// Reserved income band (green→teal→cyan): hue [113, 228]. Expense/unknown hues skip it.
const INCOME_BAND_START = 113;
const INCOME_BAND_WIDTH = 115;
// Sub-category lightness offsets off the parent L (alternating lighter/darker), so a
// child reads as the same hue family at a distinct step.
const CHILD_L_OFFSETS = [0.16, -0.16, 0.26, -0.26, 0.1, -0.1, 0.2, -0.2] as const;
// 3rd-level (grandchild, digits 5–6) nudges: a second, smaller lightness step off the
// child's shade — still the root hue, distinct from both the child and its siblings.
const GRANDCHILD_L_OFFSETS = [0.07, -0.07, 0.11, -0.11] as const;

interface ParentColor { H: number; L: number; C: number }
// Pluggy parent (2-digit prefix) → its {L,C,H} in the palette. Hue follows what the money
// IS: income (01 renda green, 03 investimento teal) green/teal; financial/money-system
// (02 loans, 06 obligations, 15 taxes, 16 fees, 20 insurance) blue; everything else
// (consumption) warm. Parents 04/05 (transfers) have no entry → the transferências blue
// token. Mirrors the token generator.
const PLUGGY_PARENT_COLOR: Record<string, ParentColor> = {
  '10': { H: 360, L: 0.5, C: 0.151 }, '11': { H: 40, L: 0.549, C: 0.15 },
  '12': { H: 294, L: 0.741, C: 0.099 }, '13': { H: 342, L: 0.659, C: 0.135 },
  '14': { H: 300, L: 0.659, C: 0.135 }, '15': { H: 234, L: 0.66, C: 0.136 },
  '16': { H: 234, L: 0.739, C: 0.1 }, '17': { H: 45, L: 0.42, C: 0.1 },
  '18': { H: 24, L: 0.579, C: 0.142 }, '19': { H: 62, L: 0.621, C: 0.13 },
  '20': { H: 270, L: 0.501, C: 0.15 }, '21': { H: 294, L: 0.42, C: 0.131 },
  '01': { H: 150, L: 0.58, C: 0.15 }, '02': { H: 234, L: 0.42, C: 0.087 },
  '03': { H: 190, L: 0.659, C: 0.11 }, '06': { H: 234, L: 0.5, C: 0.104 },
  '07': { H: 18, L: 0.741, C: 0.1 }, '08': { H: 83, L: 0.695, C: 0.088 },
  '09': { H: 90, L: 0.501, C: 0.102 },
};

/** Strong 53-bit string hash (cyrb53) — well-distributed so distinct ids don't cluster. */
function cyrb53(str: string): number {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  // Mask h2 to 21 bits so the result stays a SAFE 53-bit integer — without this the value
  // exceeds Number.MAX_SAFE_INTEGER and the low bits (used by `% CHILD_L_OFFSETS.length`
  // and `% 2450`) are lost, collapsing every user-category to the same shade.
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Color for a category id in breakdowns / legends. **Deterministic + hierarchical:** a
 * known canonical chip keeps its palette token; an 8-digit Pluggy id takes its parent's
 * {L,C,H} and, for a sub-category, a stable LIGHTNESS shade off it — so parents are
 * maximally distinct and children are lighter/darker shades of their parent. A 3rd-level
 * id (digits 5–6) takes a second, smaller lightness step off its child's shade. Any other id
 * (user-typed `userCategory`) gets a stable full-wheel hue + shade from its own hash. The
 * SAME id always resolves to the SAME color.
 */
export function hashToCategoryColor(id: string | null | undefined): string {
  if (!id) return categoryAccent(FALLBACK_CATEGORY);

  // A canonical chip id resolves to its distinct palette token color.
  const direct = CATEGORY_BY_ID.get(id);
  if (direct) return categoryAccent(direct);

  // A Pluggy id: take the parent's {L,C,H}; sub-categories shade off it.
  if (/^\d{8}$/.test(id)) {
    const prefix = id.slice(0, 2);
    const pnum = parseInt(prefix, 10);
    if (!pnum || pnum >= 99) return token('--category-outros', '#A8ABA8'); // Outros → muted neutral
    const p = PLUGGY_PARENT_COLOR[prefix];
    if (!p) {
      // 04/05 are the real transfer parents → transfer blue. Any OTHER unmapped prefix
      // (a future Pluggy parent like 22) is genuinely unknown → neutral, not "transfers".
      if (prefix === '04' || prefix === '05') return token('--category-transferencias', '#5B7FB8');
      return token('--category-outros', '#A8ABA8');
    }
    const parentKey = `${prefix}000000`;
    if (id === parentKey) return oklchToHex(p.L, p.C, p.H);
    // Sub-categories: index by the `YY` digit so siblings get DISTINCT shades.
    const childIdx = parseInt(id.slice(2, 4), 10) || 1; // 01, 02, …
    const offset = CHILD_L_OFFSETS[(childIdx - 1) % CHILD_L_OFFSETS.length] ?? 0;
    const childL = Math.max(0.3, Math.min(0.84, p.L + offset));
    // 3rd level (digits 5–6): a second, smaller lightness nudge off the child's
    // shade — same root hue, distinct from the child and from grandchild siblings.
    // When the nudge would leave the envelope (the child sits at a clamp bound),
    // flip its direction instead of clamping into the child's own shade.
    const grandIdx = parseInt(id.slice(4, 6), 10) || 0;
    if (grandIdx === 0) return oklchToHex(childL, p.C, p.H);
    const grandOffset = GRANDCHILD_L_OFFSETS[(grandIdx - 1) % GRANDCHILD_L_OFFSETS.length] ?? 0;
    let grandL = childL + grandOffset;
    if (grandL > 0.84 || grandL < 0.3) grandL = childL - grandOffset;
    return oklchToHex(Math.max(0.3, Math.min(0.84, grandL)), p.C, p.H);
  }

  // Any other id → a stable hue + shade from its own hash, hashed OUTSIDE the reserved
  // green/teal income band (hue ~113–228) so a random user category never reads as income.
  const h = cyrb53(id);
  const t = (h % 2450) / 10; // 0.0 .. 244.9 over the allowed (non-income) span
  const hue = t < INCOME_BAND_START ? t : t + INCOME_BAND_WIDTH; // skip the reserved arc
  const offset = CHILD_L_OFFSETS[h % CHILD_L_OFFSETS.length] ?? 0;
  const lightness = Math.max(0.42, Math.min(0.74, DEFAULT_L + offset * 0.5));
  return oklchToHex(lightness, DEFAULT_C, hue);
}
